import { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Heading,
  Card,
  CardBody,
  CardHeader,
  VStack,
  FormControl,
  FormLabel,
  FormHelperText,
  Input,
  Button,
  Switch,
  Divider,
  Badge,
  IconButton,
  Flex,
  Text,
  Code,
  HStack,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
  useColorModeValue,
  SimpleGrid,
  Icon,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Skeleton,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  useDisclosure,
  useToast,
  Checkbox,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Avatar,
  Select,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Tooltip,
  Portal,
} from '@chakra-ui/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlus,
  faTrash,
  faCog,
  faShieldAlt,
  faUser,
  faUsers,
  faExclamationTriangle,
  faBan,
  faCheckCircle,
  faDatabase,
  faEdit,
  faUserPlus,
  faEllipsisV,
  faInfoCircle,
  faKey,
  faSync,
  faSave,
} from '@fortawesome/free-solid-svg-icons';
import { useRecoilValue, useSetRecoilState, useRecoilState } from 'recoil';
import { userAtom, isAdminSelector } from '../state/userAtom';
import { licenseAtom } from '../state/licenseAtom';
import { botFilterAtom } from '../state/botFilterAtom';
import { licenseApi } from '../api/license';

function SettingsPage() {
  const textSecondary = useColorModeValue('gray.500', 'gray.400');
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.100', 'gray.700');
  const hoverBg = useColorModeValue('gray.50', 'whiteAlpha.50');
  const inputBg = useColorModeValue('white', 'gray.700');
  const dangerBg = useColorModeValue('red.50', 'red.900');

  const user = useRecoilValue(userAtom);
  const setUser = useSetRecoilState(userAtom);
  const isAdmin = useRecoilValue(isAdminSelector);
  const setLicense = useSetRecoilState(licenseAtom);
  const [filterBots, setFilterBots] = useRecoilState(botFilterAtom);

  // URL-based tab selection
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');

  // Tab indices
  const TAB_INDICES = useMemo(() => {
    // Managers only see Account tab
    // Admins see all tabs: Tracking, Data, Account, Users, License
    if (isAdmin) {
      return ['tracking', 'data', 'account', 'users', 'license'];
    }
    return ['account'];
  }, [isAdmin]);

  const tabIndex = useMemo(() => {
    const idx = TAB_INDICES.indexOf(tabParam);
    return idx >= 0 ? idx : 0;
  }, [tabParam, TAB_INDICES]);

  const handleTabChange = (index) => {
    const tabName = TAB_INDICES[index];
    if (tabName) {
      setSearchParams({ tab: tabName });
    }
  };

  // Account form state
  const [accountForm, setAccountForm] = useState({
    name: '',
    email: '',
  });
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [isSavingProfile, setIsSavingProfile] = useState(false);
  const [isSavingPassword, setIsSavingPassword] = useState(false);

  // Initialize account form when user loads
  useEffect(() => {
    if (user) {
      setAccountForm({
        name: user.name || '',
        email: user.email || '',
      });
    }
  }, [user]);

  // Delete data modal
  const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteOptions, setDeleteOptions] = useState({
    links: false,
    tracking: false,
    campaigns: false,
    ads: false,
    zones: false,
    sites: false,
  });
  const [confirmText, setConfirmText] = useState('');
  const toast = useToast();

  const CONFIRM_WORD = 'DELETE';

  // IP Exclusion state
  const [excludedIps, setExcludedIps] = useState([]);
  const [currentUserIp, setCurrentUserIp] = useState('');
  const [newIp, setNewIp] = useState('');
  const [isLoadingIps, setIsLoadingIps] = useState(false);
  const [isAddingIp, setIsAddingIp] = useState(false);
  const [isTogglingCurrentIp, setIsTogglingCurrentIp] = useState(false);

  // Check if current user's IP is excluded
  const isMyIpExcluded = currentUserIp && excludedIps.includes(currentUserIp);

  // Tracking settings state
  const [trackingSettings, setTrackingSettings] = useState({
    trackPageViews: true,
    trackOutboundLinks: true,
    trackDownloads: true,
    sessionTimeout: 30,
    anonymizeIp: false,
    respectDnt: true,
    visibilityAwareTime: true,
  });
  const [isLoadingTrackingSettings, setIsLoadingTrackingSettings] = useState(true);
  const [isSavingTrackingSettings, setIsSavingTrackingSettings] = useState(false);
  const [trackingSettingsChanged, setTrackingSettingsChanged] = useState(false);

  // Fetch excluded IPs, current IP, and tracking settings on mount
  useEffect(() => {
    fetchExcludedIps();
    fetchCurrentIp();
    fetchTrackingSettings();
  }, []);

  const fetchExcludedIps = async () => {
    setIsLoadingIps(true);
    try {
      const serverUrl = window.location.origin.replace(':3058', ':3059');
      const token = localStorage.getItem('wr_token');
      const response = await fetch(`${serverUrl}/api/settings/excluded-ips`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      const data = await response.json();
      if (response.ok) {
        setExcludedIps(data.data || []);
      }
    } catch (error) {
      console.error('Failed to fetch excluded IPs:', error);
    } finally {
      setIsLoadingIps(false);
    }
  };

  const fetchCurrentIp = async () => {
    try {
      const serverUrl = window.location.origin.replace(':3058', ':3059');
      const token = localStorage.getItem('wr_token');
      const response = await fetch(`${serverUrl}/api/settings/my-ip`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      const data = await response.json();
      if (response.ok && data.ip) {
        setCurrentUserIp(data.ip);
      }
    } catch (error) {
      console.error('Failed to fetch current IP:', error);
    }
  };

  const fetchTrackingSettings = async () => {
    setIsLoadingTrackingSettings(true);
    try {
      const serverUrl = window.location.origin.replace(':3058', ':3059');
      const token = localStorage.getItem('wr_token');
      const response = await fetch(`${serverUrl}/api/settings/tracking`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      const data = await response.json();
      if (response.ok && data.data) {
        setTrackingSettings(data.data);
      }
    } catch (error) {
      console.error('Failed to fetch tracking settings:', error);
    } finally {
      setIsLoadingTrackingSettings(false);
    }
  };

  const handleTrackingSettingChange = (key, value) => {
    setTrackingSettings(prev => ({ ...prev, [key]: value }));
    setTrackingSettingsChanged(true);
  };

  const handleSaveTrackingSettings = async () => {
    setIsSavingTrackingSettings(true);
    try {
      const serverUrl = window.location.origin.replace(':3058', ':3059');
      const token = localStorage.getItem('wr_token');
      // Ensure sessionTimeout is a valid number before saving
      const settingsToSave = {
        ...trackingSettings,
        sessionTimeout: parseInt(trackingSettings.sessionTimeout) || 30,
      };
      const response = await fetch(`${serverUrl}/api/settings/tracking`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(settingsToSave),
      });
      const data = await response.json();
      if (response.ok) {
        // Update local state with the validated values
        setTrackingSettings(settingsToSave);
        toast({
          title: 'Settings saved',
          description: 'Your tracking settings have been updated.',
          status: 'success',
          duration: 3000,
        });
        setTrackingSettingsChanged(false);
      } else {
        throw new Error(data.error);
      }
    } catch (error) {
      toast({
        title: 'Error saving settings',
        description: error.message || 'Failed to save tracking settings',
        status: 'error',
        duration: 5000,
      });
    } finally {
      setIsSavingTrackingSettings(false);
    }
  };

  const handleAddIp = async () => {
    if (!newIp.trim()) return;
    setIsAddingIp(true);
    try {
      const serverUrl = window.location.origin.replace(':3058', ':3059');
      const token = localStorage.getItem('wr_token');
      const response = await fetch(`${serverUrl}/api/settings/excluded-ips/add`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ ip: newIp.trim() }),
      });
      const data = await response.json();
      if (response.ok) {
        setExcludedIps(data.data || []);
        setNewIp('');
        toast({
          title: 'IP added',
          description: `${newIp.trim()} added to exclusion list`,
          status: 'success',
          duration: 3000,
        });
      } else {
        throw new Error(data.error);
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to add IP',
        status: 'error',
        duration: 3000,
      });
    } finally {
      setIsAddingIp(false);
    }
  };

  const handleRemoveIp = async (ip) => {
    try {
      const serverUrl = window.location.origin.replace(':3058', ':3059');
      const token = localStorage.getItem('wr_token');
      const response = await fetch(`${serverUrl}/api/settings/excluded-ips/${encodeURIComponent(ip)}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      const data = await response.json();
      if (response.ok) {
        setExcludedIps(data.data || []);
        toast({
          title: 'IP removed',
          description: `${ip} removed from exclusion list`,
          status: 'success',
          duration: 3000,
        });
      } else {
        throw new Error(data.error);
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to remove IP',
        status: 'error',
        duration: 3000,
      });
    }
  };

  const handleToggleMyIp = async () => {
    setIsTogglingCurrentIp(true);
    try {
      const serverUrl = window.location.origin.replace(':3058', ':3059');
      const token = localStorage.getItem('wr_token');

      if (isMyIpExcluded) {
        // Remove current IP from exclusion list
        const response = await fetch(`${serverUrl}/api/settings/excluded-ips/${encodeURIComponent(currentUserIp)}`, {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` },
        });
        const data = await response.json();
        if (response.ok) {
          setExcludedIps(data.data || []);
          toast({
            title: 'Your IP included',
            description: `Your IP (${currentUserIp}) will now be tracked`,
            status: 'info',
            duration: 3000,
          });
        } else {
          throw new Error(data.error);
        }
      } else {
        // Add current IP to exclusion list
        const response = await fetch(`${serverUrl}/api/settings/excluded-ips/add-current`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
        });
        const data = await response.json();
        if (response.ok) {
          setExcludedIps(data.data || []);
          if (data.currentIp) {
            setCurrentUserIp(data.currentIp);
          }
          toast({
            title: 'Your IP excluded',
            description: data.message || `Your IP (${data.currentIp}) has been excluded from tracking`,
            status: 'success',
            duration: 3000,
          });
        } else {
          throw new Error(data.error);
        }
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to update IP exclusion',
        status: 'error',
        duration: 3000,
      });
    } finally {
      setIsTogglingCurrentIp(false);
    }
  };

  const handleDeleteOptionChange = (option) => {
    setDeleteOptions(prev => ({ ...prev, [option]: !prev[option] }));
  };

  const resetDeleteModal = () => {
    setDeleteOptions({ links: false, tracking: false, campaigns: false, ads: false, zones: false, sites: false });
    setConfirmText('');
    onDeleteClose();
  };

  const hasSelectedOption = deleteOptions.links || deleteOptions.tracking || deleteOptions.campaigns || deleteOptions.ads || deleteOptions.zones || deleteOptions.sites;
  const isConfirmValid = confirmText === CONFIRM_WORD;

  const handleDeleteData = async () => {
    if (!hasSelectedOption || !isConfirmValid) return;

    setIsDeleting(true);
    try {
      const serverUrl = window.location.origin.replace(':3058', ':3059');
      const token = localStorage.getItem('wr_token');

      const response = await fetch(`${serverUrl}/api/sites/admin/purge-data`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(deleteOptions),
      });

      const data = await response.json();

      if (response.ok) {
        const deletedItems = [];
        if (deleteOptions.links) deletedItems.push('UTM links');
        if (deleteOptions.tracking) deletedItems.push('tracking data');
        if (deleteOptions.campaigns) deletedItems.push('campaigns');
        if (deleteOptions.ads) deletedItems.push('ads');
        if (deleteOptions.zones) deletedItems.push('zones');
        if (deleteOptions.sites) deletedItems.push('sites');

        toast({
          title: 'Data deleted',
          description: `Successfully deleted: ${deletedItems.join(', ')}.`,
          status: 'success',
          duration: 5000,
          isClosable: true,
        });
        resetDeleteModal();
      } else {
        throw new Error(data.error || 'Failed to delete data');
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to delete data. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const handleSaveProfile = async () => {
    if (!accountForm.name.trim()) {
      toast({ title: 'Name is required', status: 'error', duration: 3000 });
      return;
    }

    setIsSavingProfile(true);
    try {
      const serverUrl = window.location.origin.replace(':3058', ':3059');
      const token = localStorage.getItem('wr_token');

      const response = await fetch(`${serverUrl}/api/auth/profile`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: accountForm.name,
          email: accountForm.email,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setUser(prev => ({ ...prev, name: accountForm.name, email: accountForm.email }));
        toast({ title: 'Profile updated', status: 'success', duration: 3000 });
      } else {
        throw new Error(data.error || 'Failed to update profile');
      }
    } catch (error) {
      toast({ title: 'Error', description: error.message, status: 'error', duration: 5000 });
    } finally {
      setIsSavingProfile(false);
    }
  };

  const handleChangePassword = async () => {
    if (!passwordForm.currentPassword) {
      toast({ title: 'Current password is required', status: 'error', duration: 3000 });
      return;
    }

    if (passwordForm.newPassword.length < 6) {
      toast({ title: 'New password must be at least 6 characters', status: 'error', duration: 3000 });
      return;
    }

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      toast({ title: 'Passwords do not match', status: 'error', duration: 3000 });
      return;
    }

    setIsSavingPassword(true);
    try {
      const serverUrl = window.location.origin.replace(':3058', ':3059');
      const token = localStorage.getItem('wr_token');

      const response = await fetch(`${serverUrl}/api/auth/change-password`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          currentPassword: passwordForm.currentPassword,
          newPassword: passwordForm.newPassword,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        toast({ title: 'Password changed', description: 'Your password has been updated.', status: 'success', duration: 3000 });
        setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
      } else {
        throw new Error(data.error || 'Failed to change password');
      }
    } catch (error) {
      toast({ title: 'Error', description: error.message, status: 'error', duration: 5000 });
    } finally {
      setIsSavingPassword(false);
    }
  };

  return (
    <Box>
      {/* Page Header */}
      <Box mb={{ base: 6, md: 8 }}>
        <Heading
          size={{ base: 'lg', md: 'xl' }}
          bgGradient="linear(to-r, brand.500, accent.500)"
          bgClip="text"
          fontWeight="700"
        >
          Settings
        </Heading>
        <Text color={textSecondary} mt={1}>
          Configure your account and tracking preferences
        </Text>
      </Box>

      <Tabs colorScheme="brand" variant="soft-rounded" index={tabIndex} onChange={handleTabChange}>
        <TabList
          flexWrap="wrap"
          gap={2}
          bg={cardBg}
          p={2}
          borderRadius="2xl"
          border="1px solid"
          borderColor={borderColor}
          mb={6}
        >
          {isAdmin && (
            <Tab borderRadius="xl" fontSize="sm">
              <HStack spacing={2}>
                <FontAwesomeIcon icon={faCog} />
                <Text display={{ base: 'none', sm: 'inline' }}>Tracking</Text>
              </HStack>
            </Tab>
          )}
          {isAdmin && (
            <Tab borderRadius="xl" fontSize="sm">
              <HStack spacing={2}>
                <FontAwesomeIcon icon={faDatabase} />
                <Text display={{ base: 'none', sm: 'inline' }}>Data</Text>
              </HStack>
            </Tab>
          )}
          <Tab borderRadius="xl" fontSize="sm">
            <HStack spacing={2}>
              <FontAwesomeIcon icon={faUser} />
              <Text display={{ base: 'none', sm: 'inline' }}>Account</Text>
            </HStack>
          </Tab>
          {isAdmin && (
            <Tab borderRadius="xl" fontSize="sm">
              <HStack spacing={2}>
                <FontAwesomeIcon icon={faUsers} />
                <Text display={{ base: 'none', sm: 'inline' }}>Users</Text>
              </HStack>
            </Tab>
          )}
          {isAdmin && (
            <Tab borderRadius="xl" fontSize="sm">
              <HStack spacing={2}>
                <FontAwesomeIcon icon={faKey} />
                <Text display={{ base: 'none', sm: 'inline' }}>License</Text>
              </HStack>
            </Tab>
          )}
        </TabList>

        <TabPanels>
          {/* Tracking Tab (Admin Only) */}
          {isAdmin && (
            <TabPanel px={0}>
              <VStack spacing={6} align="stretch">
                {/* Save Button */}
              {trackingSettingsChanged && (
                <Flex justify="flex-end">
                  <Button
                    leftIcon={<FontAwesomeIcon icon={faSave} />}
                    colorScheme="brand"
                    onClick={handleSaveTrackingSettings}
                    isLoading={isSavingTrackingSettings}
                    loadingText="Saving..."
                    borderRadius="xl"
                  >
                    Save Changes
                  </Button>
                </Flex>
              )}

              <SimpleGrid columns={{ base: 1, lg: 3 }} spacing={6}>
                <Card bg={cardBg} border="1px solid" borderColor={borderColor}>
                  <CardHeader pb={2}>
                    <HStack spacing={2}>
                      <Box p={2} bgGradient="linear(135deg, brand.500 0%, accent.500 100%)" borderRadius="lg">
                        <Icon color="white" boxSize={4}>
                          <FontAwesomeIcon icon={faCog} />
                        </Icon>
                      </Box>
                      <Heading size="md">Tracking Options</Heading>
                    </HStack>
                  </CardHeader>
                  <CardBody>
                    {isLoadingTrackingSettings ? (
                      <VStack spacing={4}>
                        <Skeleton height="60px" borderRadius="lg" />
                        <Skeleton height="60px" borderRadius="lg" />
                        <Skeleton height="60px" borderRadius="lg" />
                      </VStack>
                    ) : (
                      <VStack align="stretch" spacing={0} divider={<Divider borderColor={borderColor} />}>
                        <SettingToggle
                          label="Track Page Views"
                          description="Automatically track all page views"
                          isChecked={trackingSettings.trackPageViews}
                          onChange={(e) => handleTrackingSettingChange('trackPageViews', e.target.checked)}
                        />
                        <SettingToggle
                          label="Track Outbound Links"
                          description="Track clicks on external links"
                          isChecked={trackingSettings.trackOutboundLinks}
                          onChange={(e) => handleTrackingSettingChange('trackOutboundLinks', e.target.checked)}
                        />
                        <SettingToggle
                          label="Track Downloads"
                          description="Track file downloads (PDF, ZIP, etc.)"
                          isChecked={trackingSettings.trackDownloads}
                          onChange={(e) => handleTrackingSettingChange('trackDownloads', e.target.checked)}
                        />
                        <FormControl display="flex" justifyContent="space-between" alignItems="center" py={4}>
                          <Box>
                            <FormLabel mb={0} fontWeight="500">Session Timeout</FormLabel>
                            <FormHelperText mt={1} fontSize="sm">Time before a new session is started (minutes)</FormHelperText>
                          </Box>
                          <Input
                            type="number"
                            value={trackingSettings.sessionTimeout}
                            onChange={(e) => handleTrackingSettingChange('sessionTimeout', e.target.value === '' ? '' : parseInt(e.target.value))}
                            onBlur={(e) => {
                              if (e.target.value === '' || isNaN(parseInt(e.target.value))) {
                                handleTrackingSettingChange('sessionTimeout', 30);
                              }
                            }}
                            min={1}
                            max={1440}
                            maxW="100px"
                            size="sm"
                            bg={inputBg}
                            borderRadius="lg"
                          />
                        </FormControl>
                        <SettingToggle
                          label="Visibility-Aware Page Time"
                          description="Only count time when the tab is visible and the visitor is active (more accurate, may show lower times)"
                          isChecked={trackingSettings.visibilityAwareTime}
                          onChange={(e) => handleTrackingSettingChange('visibilityAwareTime', e.target.checked)}
                        />
                        <SettingToggle
                          label="Filter Bot Traffic"
                          description="Exclude suspected bots from visitor and pageview stats (sessions with 0 duration and a single pageview). Applies to Web Stats, Dashboard, and Reports. It does not change ad impression or click counts, which are tracked and filtered separately."
                          isChecked={filterBots}
                          onChange={(e) => setFilterBots(e.target.checked)}
                        />
                      </VStack>
                    )}
                  </CardBody>
                </Card>

                <Card bg={cardBg} border="1px solid" borderColor={borderColor}>
                  <CardHeader pb={2}>
                    <HStack spacing={2}>
                      <Box p={2} bgGradient="linear(135deg, purple.500 0%, pink.500 100%)" borderRadius="lg">
                        <Icon color="white" boxSize={4}>
                          <FontAwesomeIcon icon={faShieldAlt} />
                        </Icon>
                      </Box>
                      <Heading size="md">IP & Privacy</Heading>
                    </HStack>
                  </CardHeader>
                  <CardBody>
                    {isLoadingTrackingSettings ? (
                      <VStack spacing={4}>
                        <Skeleton height="60px" borderRadius="lg" />
                        <Skeleton height="60px" borderRadius="lg" />
                      </VStack>
                    ) : (
                      <VStack align="stretch" spacing={0} divider={<Divider borderColor={borderColor} />}>
                        <SettingToggle
                          label="Anonymize IP Addresses"
                          description="Mask the last octet of visitor IPs"
                          isChecked={trackingSettings.anonymizeIp}
                          onChange={(e) => handleTrackingSettingChange('anonymizeIp', e.target.checked)}
                        />
                        <SettingToggle
                          label="Respect Do Not Track"
                          description="Don't track visitors with DNT enabled"
                          isChecked={trackingSettings.respectDnt}
                          onChange={(e) => handleTrackingSettingChange('respectDnt', e.target.checked)}
                        />
                      </VStack>
                    )}
                  </CardBody>
                </Card>

                {/* IP Exclusion Card */}
                <Card bg={cardBg} border="1px solid" borderColor={borderColor}>
                <CardHeader pb={2}>
                  <Flex justify="space-between" align="center" flexWrap="wrap" gap={3}>
                    <HStack spacing={2}>
                      <Box p={2} bgGradient="linear(135deg, red.500 0%, orange.500 100%)" borderRadius="lg">
                        <Icon color="white" boxSize={4}>
                          <FontAwesomeIcon icon={faBan} />
                        </Icon>
                      </Box>
                      <Heading size="md">Excluded IP Addresses</Heading>
                    </HStack>
                    <Button
                      leftIcon={<FontAwesomeIcon icon={isMyIpExcluded ? faCheckCircle : faBan} />}
                      colorScheme={isMyIpExcluded ? 'green' : 'brand'}
                      variant={isMyIpExcluded ? 'outline' : 'solid'}
                      size="sm"
                      borderRadius="lg"
                      onClick={handleToggleMyIp}
                      isLoading={isTogglingCurrentIp}
                      loadingText={isMyIpExcluded ? 'Including...' : 'Excluding...'}
                    >
                      {isMyIpExcluded ? 'Include My IP' : 'Exclude My IP'}
                    </Button>
                  </Flex>
                </CardHeader>
                <CardBody>
                  <Text fontSize="sm" color={textSecondary} mb={4}>
                    Traffic from these IP addresses will not be tracked. Useful for excluding your own visits and internal traffic.
                  </Text>

                  {/* Current IP indicator */}
                  {currentUserIp && (
                    <Alert
                      status={isMyIpExcluded ? 'success' : 'info'}
                      borderRadius="xl"
                      mb={4}
                      py={2}
                    >
                      <AlertIcon />
                      <Box>
                        <Text fontSize="sm">
                          Your IP: <Code fontWeight="bold">{currentUserIp}</Code>
                          {isMyIpExcluded ? ' (excluded)' : ' (being tracked)'}
                        </Text>
                      </Box>
                    </Alert>
                  )}

                  {/* Add IP Form */}
                  <HStack mb={4}>
                    <Input
                      placeholder="Enter IP address (e.g., 192.168.1.1)"
                      value={newIp}
                      onChange={(e) => setNewIp(e.target.value)}
                      bg={inputBg}
                      borderRadius="lg"
                      onKeyPress={(e) => e.key === 'Enter' && handleAddIp()}
                    />
                    <Button
                      leftIcon={<FontAwesomeIcon icon={faPlus} />}
                      colorScheme="brand"
                      onClick={handleAddIp}
                      isLoading={isAddingIp}
                      borderRadius="lg"
                      flexShrink={0}
                    >
                      Add
                    </Button>
                  </HStack>

                  {/* IP List */}
                  {isLoadingIps ? (
                    <VStack spacing={2}>
                      <Skeleton height="40px" borderRadius="lg" />
                      <Skeleton height="40px" borderRadius="lg" />
                    </VStack>
                  ) : excludedIps.length > 0 ? (
                    <VStack align="stretch" spacing={2}>
                      {excludedIps.map((ip) => (
                        <Flex
                          key={ip}
                          justify="space-between"
                          align="center"
                          p={3}
                          bg={hoverBg}
                          borderRadius="lg"
                          border="1px solid"
                          borderColor={borderColor}
                        >
                          <HStack spacing={3}>
                            <Icon color="red.400" boxSize={4}>
                              <FontAwesomeIcon icon={faBan} />
                            </Icon>
                            <Code fontSize="sm" bg="transparent">{ip}</Code>
                          </HStack>
                          <IconButton
                            icon={<FontAwesomeIcon icon={faTrash} />}
                            size="sm"
                            variant="ghost"
                            colorScheme="red"
                            aria-label={`Remove ${ip}`}
                            onClick={() => handleRemoveIp(ip)}
                            borderRadius="lg"
                          />
                        </Flex>
                      ))}
                    </VStack>
                  ) : (
                    <Box
                      p={6}
                      textAlign="center"
                      bg={hoverBg}
                      borderRadius="xl"
                      border="1px dashed"
                      borderColor={borderColor}
                    >
                      <Icon color={textSecondary} boxSize={8} mb={2}>
                        <FontAwesomeIcon icon={faBan} />
                      </Icon>
                      <Text color={textSecondary} fontSize="sm">
                        No IP addresses excluded yet.
                      </Text>
                      <Text color={textSecondary} fontSize="xs" mt={1}>
                        Click "Exclude My IP" to add your current IP address.
                      </Text>
                    </Box>
                  )}

                  <Text fontSize="xs" color={textSecondary} mt={4}>
                    Supports IPv4, IPv6, and CIDR notation (e.g., 192.168.1.0/24)
                  </Text>
                </CardBody>
              </Card>
              </SimpleGrid>
              </VStack>
            </TabPanel>
          )}

          {/* Data Tab (Admin Only) */}
          {isAdmin && (
            <TabPanel px={0}>
              <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6} alignItems="start">
              <Card bg={cardBg} border="1px solid" borderColor={borderColor}>
                <CardHeader pb={2}>
                  <HStack spacing={2}>
                    <Box p={2} bgGradient="linear(135deg, cyan.500 0%, blue.500 100%)" borderRadius="lg">
                      <Icon color="white" boxSize={4}>
                        <FontAwesomeIcon icon={faDatabase} />
                      </Icon>
                    </Box>
                    <Heading size="md">Data Retention</Heading>
                  </HStack>
                </CardHeader>
                <CardBody>
                  <FormControl>
                    <FormLabel>Keep raw data for</FormLabel>
                    <HStack>
                      <Input type="number" defaultValue={365} maxW="100px" bg={inputBg} borderRadius="lg" />
                      <Text>days</Text>
                      <Button colorScheme="brand" size="sm" borderRadius="lg">
                        Save
                      </Button>
                    </HStack>
                    <FormHelperText>
                      Raw pageview and event data will be deleted after this period. Aggregated data is kept indefinitely.
                    </FormHelperText>
                  </FormControl>
                </CardBody>
              </Card>

              {isAdmin && (
                <Card bg={cardBg} border="1px solid" borderColor={borderColor} borderTopWidth="3px" borderTopColor="red.500">
                  <CardHeader pb={2}>
                    <HStack spacing={2}>
                      <Box p={2} bg="red.100" borderRadius="lg">
                        <Icon color="red.500" boxSize={4}>
                          <FontAwesomeIcon icon={faExclamationTriangle} />
                        </Icon>
                      </Box>
                      <Heading size="md" color="red.500">Danger Zone</Heading>
                      <Badge colorScheme="red" fontSize="xs">Admin Only</Badge>
                    </HStack>
                  </CardHeader>
                  <CardBody>
                    <Flex
                      direction={{ base: 'column', md: 'row' }}
                      gap={4}
                      align="stretch"
                    >
                      <Alert status="warning" borderRadius="xl" flex={1}>
                        <AlertIcon />
                        <Box>
                          <AlertTitle>Caution</AlertTitle>
                          <AlertDescription fontSize="sm">
                            These actions are irreversible. Please proceed with caution.
                          </AlertDescription>
                        </Box>
                      </Alert>
                      <Flex
                        justify="space-between"
                        align="center"
                        p={4}
                        bg={dangerBg}
                        borderRadius="xl"
                        flex={1}
                        gap={3}
                      >
                        <HStack spacing={3}>
                          <Icon color="red.500" boxSize={5}>
                            <FontAwesomeIcon icon={faTrash} />
                          </Icon>
                          <Box>
                            <HStack spacing={2}>
                              <Text fontWeight="600">Delete Data</Text>
                              <Tooltip
                                label="Permanently delete selected data from the database. This includes analytics, ad performance metrics, and configuration. This action cannot be undone."
                                placement="top"
                                hasArrow
                                maxW="280px"
                              >
                                <Box as="span" cursor="help">
                                  <Icon color={textSecondary} boxSize={3.5}>
                                    <FontAwesomeIcon icon={faInfoCircle} />
                                  </Icon>
                                </Box>
                              </Tooltip>
                            </HStack>
                            <Text fontSize="sm" color={textSecondary}>
                              Delete tracking data, campaigns, or sites.
                            </Text>
                          </Box>
                        </HStack>
                        <Button
                          colorScheme="red"
                          variant="outline"
                          size="sm"
                          borderRadius="lg"
                          onClick={onDeleteOpen}
                          flexShrink={0}
                        >
                          Delete Data
                        </Button>
                      </Flex>
                    </Flex>
                  </CardBody>
                </Card>
              )}
              </SimpleGrid>
            </TabPanel>
          )}

          {/* Account Tab */}
          <TabPanel px={0}>
            <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6}>
              <Card bg={cardBg} border="1px solid" borderColor={borderColor}>
                <CardHeader pb={2}>
                  <HStack spacing={2}>
                    <Box p={2} bgGradient="linear(135deg, brand.500 0%, accent.500 100%)" borderRadius="lg">
                      <Icon color="white" boxSize={4}>
                        <FontAwesomeIcon icon={faUser} />
                      </Icon>
                    </Box>
                    <Heading size="md">Account Settings</Heading>
                  </HStack>
                </CardHeader>
                <CardBody>
                  <VStack align="stretch" spacing={4}>
                    <FormControl>
                      <FormLabel>Email</FormLabel>
                      <Input
                        type="email"
                        value={accountForm.email}
                        onChange={(e) => setAccountForm(prev => ({ ...prev, email: e.target.value }))}
                        bg={inputBg}
                        borderRadius="lg"
                      />
                    </FormControl>
                    <FormControl>
                      <FormLabel>Name</FormLabel>
                      <Input
                        value={accountForm.name}
                        onChange={(e) => setAccountForm(prev => ({ ...prev, name: e.target.value }))}
                        bg={inputBg}
                        borderRadius="lg"
                      />
                    </FormControl>
                    <Button
                      colorScheme="brand"
                      alignSelf="flex-start"
                      borderRadius="lg"
                      onClick={handleSaveProfile}
                      isLoading={isSavingProfile}
                    >
                      Save Changes
                    </Button>
                  </VStack>
                </CardBody>
              </Card>

              <Card bg={cardBg} border="1px solid" borderColor={borderColor}>
                <CardHeader pb={2}>
                  <HStack spacing={2}>
                    <Box p={2} bgGradient="linear(135deg, orange.500 0%, red.500 100%)" borderRadius="lg">
                      <Icon color="white" boxSize={4}>
                        <FontAwesomeIcon icon={faShieldAlt} />
                      </Icon>
                    </Box>
                    <Heading size="md">Change Password</Heading>
                  </HStack>
                </CardHeader>
                <CardBody>
                  <VStack align="stretch" spacing={4}>
                    <FormControl>
                      <FormLabel>Current Password</FormLabel>
                      <Input
                        type="password"
                        value={passwordForm.currentPassword}
                        onChange={(e) => setPasswordForm(prev => ({ ...prev, currentPassword: e.target.value }))}
                        bg={inputBg}
                        borderRadius="lg"
                      />
                    </FormControl>
                    <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                      <FormControl>
                        <FormLabel>New Password</FormLabel>
                        <Input
                          type="password"
                          value={passwordForm.newPassword}
                          onChange={(e) => setPasswordForm(prev => ({ ...prev, newPassword: e.target.value }))}
                          bg={inputBg}
                          borderRadius="lg"
                        />
                      </FormControl>
                      <FormControl>
                        <FormLabel>Confirm New Password</FormLabel>
                        <Input
                          type="password"
                          value={passwordForm.confirmPassword}
                          onChange={(e) => setPasswordForm(prev => ({ ...prev, confirmPassword: e.target.value }))}
                          bg={inputBg}
                          borderRadius="lg"
                        />
                      </FormControl>
                    </SimpleGrid>
                    <Button
                      colorScheme="brand"
                      alignSelf="flex-start"
                      borderRadius="lg"
                      onClick={handleChangePassword}
                      isLoading={isSavingPassword}
                    >
                      Update Password
                    </Button>
                  </VStack>
                </CardBody>
              </Card>
            </SimpleGrid>
          </TabPanel>

          {/* Users Tab (Admin Only) */}
          {isAdmin && (
            <TabPanel px={0}>
              <UsersTab
                cardBg={cardBg}
                borderColor={borderColor}
                textSecondary={textSecondary}
                hoverBg={hoverBg}
                inputBg={inputBg}
                toast={toast}
              />
            </TabPanel>
          )}

          {/* License Tab (Admin Only) */}
          {isAdmin && (
            <TabPanel px={0}>
              <LicenseTab
                cardBg={cardBg}
                borderColor={borderColor}
                textSecondary={textSecondary}
                inputBg={inputBg}
                toast={toast}
                setLicense={setLicense}
              />
            </TabPanel>
          )}
        </TabPanels>
      </Tabs>

      {/* Delete Data Confirmation Modal */}
      <Modal isOpen={isDeleteOpen} onClose={resetDeleteModal} isCentered size="2xl">
        <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(4px)" />
        <ModalContent borderRadius="2xl" mx={4}>
          <ModalHeader>Delete Data</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Alert status="error" borderRadius="xl" mb={4}>
              <AlertIcon />
              <Box>
                <AlertTitle>This action is irreversible!</AlertTitle>
                <AlertDescription fontSize="sm">
                  Selected data will be permanently deleted and cannot be recovered.
                </AlertDescription>
              </Box>
            </Alert>

            <Text fontWeight="600" mb={3}>Select what to delete:</Text>
            <SimpleGrid columns={2} spacing={3} mb={6}>
              <Box
                p={4}
                bg={dangerBg}
                borderRadius="xl"
                border="1px solid"
                borderColor={deleteOptions.links ? 'red.300' : 'transparent'}
                cursor="pointer"
                onClick={() => handleDeleteOptionChange('links')}
              >
                <Checkbox
                  isChecked={deleteOptions.links}
                  onChange={() => handleDeleteOptionChange('links')}
                  colorScheme="red"
                  size="lg"
                >
                  <Box ml={2}>
                    <Text fontWeight="600">Link Builder</Text>
                    <Text fontSize="sm" color={textSecondary}>
                      All UTM links and their click tracking data
                    </Text>
                  </Box>
                </Checkbox>
              </Box>

              <Box
                p={4}
                bg={dangerBg}
                borderRadius="xl"
                border="1px solid"
                borderColor={deleteOptions.tracking ? 'red.300' : 'transparent'}
                cursor="pointer"
                onClick={() => handleDeleteOptionChange('tracking')}
              >
                <Checkbox
                  isChecked={deleteOptions.tracking}
                  onChange={() => handleDeleteOptionChange('tracking')}
                  colorScheme="red"
                  size="lg"
                >
                  <Box ml={2}>
                    <Text fontWeight="600">Tracking Data</Text>
                    <Text fontSize="sm" color={textSecondary}>
                      Visitors, visits, pageviews, events, impressions, and clicks
                    </Text>
                  </Box>
                </Checkbox>
              </Box>

              <Box
                p={4}
                bg={dangerBg}
                borderRadius="xl"
                border="1px solid"
                borderColor={deleteOptions.campaigns ? 'red.300' : 'transparent'}
                cursor="pointer"
                onClick={() => handleDeleteOptionChange('campaigns')}
              >
                <Checkbox
                  isChecked={deleteOptions.campaigns}
                  onChange={() => handleDeleteOptionChange('campaigns')}
                  colorScheme="red"
                  size="lg"
                >
                  <Box ml={2}>
                    <Text fontWeight="600">Campaigns</Text>
                    <Text fontSize="sm" color={textSecondary}>
                      All advertising campaigns
                    </Text>
                  </Box>
                </Checkbox>
              </Box>

              <Box
                p={4}
                bg={dangerBg}
                borderRadius="xl"
                border="1px solid"
                borderColor={deleteOptions.ads ? 'red.300' : 'transparent'}
                cursor="pointer"
                onClick={() => handleDeleteOptionChange('ads')}
              >
                <Checkbox
                  isChecked={deleteOptions.ads}
                  onChange={() => handleDeleteOptionChange('ads')}
                  colorScheme="red"
                  size="lg"
                >
                  <Box ml={2}>
                    <Text fontWeight="600">Ads</Text>
                    <Text fontSize="sm" color={textSecondary}>
                      All ad creatives and banners
                    </Text>
                  </Box>
                </Checkbox>
              </Box>

              <Box
                p={4}
                bg={dangerBg}
                borderRadius="xl"
                border="1px solid"
                borderColor={deleteOptions.zones ? 'red.300' : 'transparent'}
                cursor="pointer"
                onClick={() => handleDeleteOptionChange('zones')}
              >
                <Checkbox
                  isChecked={deleteOptions.zones}
                  onChange={() => handleDeleteOptionChange('zones')}
                  colorScheme="red"
                  size="lg"
                >
                  <Box ml={2}>
                    <Text fontWeight="600">Zones</Text>
                    <Text fontSize="sm" color={textSecondary}>
                      All ad placement zones
                    </Text>
                  </Box>
                </Checkbox>
              </Box>

              <Box
                p={4}
                bg={dangerBg}
                borderRadius="xl"
                border="1px solid"
                borderColor={deleteOptions.sites ? 'red.300' : 'transparent'}
                cursor="pointer"
                onClick={() => handleDeleteOptionChange('sites')}
              >
                <Checkbox
                  isChecked={deleteOptions.sites}
                  onChange={() => handleDeleteOptionChange('sites')}
                  colorScheme="red"
                  size="lg"
                >
                  <Box ml={2}>
                    <Text fontWeight="600">Sites</Text>
                    <Text fontSize="sm" color={textSecondary}>
                      All registered websites (will also delete related data)
                    </Text>
                  </Box>
                </Checkbox>
              </Box>
            </SimpleGrid>

            {hasSelectedOption && (
              <Box>
                <Text fontWeight="600" mb={2}>
                  Type <Code colorScheme="red" fontWeight="bold">{CONFIRM_WORD}</Code> to confirm:
                </Text>
                <Input
                  placeholder={`Type ${CONFIRM_WORD} here`}
                  value={confirmText}
                  onChange={(e) => setConfirmText(e.target.value.toUpperCase())}
                  borderColor={isConfirmValid ? 'green.300' : borderColor}
                  _focus={{ borderColor: isConfirmValid ? 'green.500' : 'red.300' }}
                  borderRadius="lg"
                />
              </Box>
            )}
          </ModalBody>
          <ModalFooter gap={3}>
            <Button variant="ghost" onClick={resetDeleteModal} borderRadius="lg">
              Cancel
            </Button>
            <Button
              colorScheme="red"
              bg="red.600"
              _hover={{ bg: 'red.700' }}
              onClick={handleDeleteData}
              isLoading={isDeleting}
              loadingText="Deleting..."
              borderRadius="lg"
              isDisabled={!hasSelectedOption || !isConfirmValid}
            >
              Delete Selected Data
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}

function SettingToggle({ label, description, isChecked, onChange, isDisabled = false }) {
  return (
    <FormControl display="flex" justifyContent="space-between" alignItems="center" py={4}>
      <Box>
        <FormLabel mb={0} fontWeight="500">{label}</FormLabel>
        <FormHelperText mt={1} fontSize="sm">{description}</FormHelperText>
      </Box>
      <Switch
        isChecked={isChecked}
        onChange={onChange}
        colorScheme="brand"
        size="lg"
        isDisabled={isDisabled}
      />
    </FormControl>
  );
}

function UsersTab({ cardBg, borderColor, textSecondary, hoverBg, inputBg, toast }) {
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const { isOpen: isUserModalOpen, onOpen: onUserModalOpen, onClose: onUserModalClose } = useDisclosure();
  const [selectedUser, setSelectedUser] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Form state for new/edit user
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: 'user',
  });

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    setIsLoading(true);
    try {
      const serverUrl = window.location.origin.replace(':3058', ':3059');
      const token = localStorage.getItem('wr_token');
      const response = await fetch(`${serverUrl}/api/auth/users`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      const data = await response.json();
      if (response.ok) {
        setUsers(data.data || []);
      }
    } catch (error) {
      console.error('Failed to fetch users:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenCreateModal = () => {
    setSelectedUser(null);
    setFormData({ name: '', email: '', password: '', role: 'user' });
    onUserModalOpen();
  };

  const handleOpenEditModal = (user) => {
    setSelectedUser(user);
    setFormData({
      name: user.name || '',
      email: user.email || '',
      password: '',
      role: user.role || 'user',
    });
    onUserModalOpen();
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async () => {
    if (!formData.name.trim() || !formData.email.trim()) {
      toast({ title: 'Name and email are required', status: 'error', duration: 3000 });
      return;
    }

    if (!selectedUser && !formData.password) {
      toast({ title: 'Password is required for new users', status: 'error', duration: 3000 });
      return;
    }

    setIsSubmitting(true);
    try {
      const serverUrl = window.location.origin.replace(':3058', ':3059');
      const token = localStorage.getItem('wr_token');

      const url = selectedUser
        ? `${serverUrl}/api/auth/users/${selectedUser.user_id}`
        : `${serverUrl}/api/auth/users`;

      const response = await fetch(url, {
        method: selectedUser ? 'PUT' : 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (response.ok) {
        toast({
          title: selectedUser ? 'User updated' : 'User created',
          status: 'success',
          duration: 3000,
        });
        onUserModalClose();
        fetchUsers();
      } else {
        throw new Error(data.error || 'Failed to save user');
      }
    } catch (error) {
      toast({ title: 'Error', description: error.message, status: 'error', duration: 5000 });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteUser = async (user) => {
    if (!window.confirm(`Are you sure you want to delete ${user.name}?`)) return;

    try {
      const serverUrl = window.location.origin.replace(':3058', ':3059');
      const token = localStorage.getItem('wr_token');

      const response = await fetch(`${serverUrl}/api/auth/users/${user.user_id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
      });

      if (response.ok) {
        toast({ title: 'User deleted', status: 'success', duration: 3000 });
        fetchUsers();
      } else {
        const data = await response.json();
        throw new Error(data.error || 'Failed to delete user');
      }
    } catch (error) {
      toast({ title: 'Error', description: error.message, status: 'error', duration: 5000 });
    }
  };

  return (
    <VStack spacing={6} align="stretch">
      <Card bg={cardBg} border="1px solid" borderColor={borderColor}>
        <CardHeader pb={2}>
          <Flex justify="space-between" align="center" flexWrap="wrap" gap={3}>
            <HStack spacing={2}>
              <Box p={2} bgGradient="linear(135deg, purple.500 0%, pink.500 100%)" borderRadius="lg">
                <Icon color="white" boxSize={4}>
                  <FontAwesomeIcon icon={faUsers} />
                </Icon>
              </Box>
              <Heading size="md">User Management</Heading>
            </HStack>
            <Button
              leftIcon={<FontAwesomeIcon icon={faUserPlus} />}
              colorScheme="brand"
              size="sm"
              borderRadius="lg"
              onClick={handleOpenCreateModal}
            >
              Add User
            </Button>
          </Flex>
        </CardHeader>
        <CardBody pt={0}>
          {isLoading ? (
            <VStack spacing={3}>
              <Skeleton height="50px" borderRadius="md" />
              <Skeleton height="50px" borderRadius="md" />
              <Skeleton height="50px" borderRadius="md" />
            </VStack>
          ) : users.length > 0 ? (
            <TableContainer>
              <Table variant="simple">
                <Thead>
                  <Tr>
                    <Th>User</Th>
                    <Th>Email</Th>
                    <Th>Role</Th>
                    <Th>Created</Th>
                    <Th width="80px"></Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {users.map((user) => (
                    <Tr key={user.user_id} _hover={{ bg: hoverBg }} transition="all 0.2s">
                      <Td>
                        <HStack spacing={3}>
                          <Avatar size="sm" name={user.name} bg="brand.500" color="white" />
                          <Text fontWeight="500">{user.name}</Text>
                        </HStack>
                      </Td>
                      <Td color={textSecondary}>{user.email}</Td>
                      <Td>
                        <Badge
                          colorScheme={user.role === 'admin' ? 'purple' : 'blue'}
                          borderRadius="full"
                        >
                          {user.role}
                        </Badge>
                      </Td>
                      <Td color={textSecondary} fontSize="sm">
                        {user.created_at ? new Date(user.created_at).toLocaleDateString() : '-'}
                      </Td>
                      <Td>
                        <Menu>
                          <MenuButton
                            as={IconButton}
                            icon={<FontAwesomeIcon icon={faEllipsisV} />}
                            variant="ghost"
                            size="sm"
                            borderRadius="lg"
                          />
                          <Portal>
                            <MenuList zIndex={9999} py={2} borderRadius="xl">
                              <MenuItem
                                icon={<FontAwesomeIcon icon={faEdit} />}
                                onClick={() => handleOpenEditModal(user)}
                                mx={2}
                                borderRadius="lg"
                                w="calc(100% - 16px)"
                              >
                                Edit
                              </MenuItem>
                              <MenuItem
                                icon={<FontAwesomeIcon icon={faTrash} />}
                                color="red.500"
                                onClick={() => handleDeleteUser(user)}
                                mx={2}
                                borderRadius="lg"
                                w="calc(100% - 16px)"
                              >
                                Delete
                              </MenuItem>
                            </MenuList>
                          </Portal>
                        </Menu>
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </TableContainer>
          ) : (
            <Text color={textSecondary} textAlign="center" py={6}>
              No users found. Add your first user to get started.
            </Text>
          )}
        </CardBody>
      </Card>

      {/* User Modal */}
      <Modal isOpen={isUserModalOpen} onClose={onUserModalClose} isCentered>
        <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(4px)" />
        <ModalContent borderRadius="2xl" mx={4}>
          <ModalHeader>{selectedUser ? 'Edit User' : 'Add New User'}</ModalHeader>
          <ModalCloseButton borderRadius="full" />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              <FormControl isRequired>
                <FormLabel>Name</FormLabel>
                <Input
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Full name"
                  bg={inputBg}
                  borderRadius="xl"
                />
              </FormControl>

              <FormControl isRequired>
                <FormLabel>Email</FormLabel>
                <Input
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="email@example.com"
                  bg={inputBg}
                  borderRadius="xl"
                />
              </FormControl>

              <FormControl isRequired={!selectedUser}>
                <FormLabel>
                  Password {selectedUser && <Text as="span" fontSize="xs" color={textSecondary}>(leave blank to keep current)</Text>}
                </FormLabel>
                <Input
                  name="password"
                  type="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder={selectedUser ? '••••••••' : 'Enter password'}
                  bg={inputBg}
                  borderRadius="xl"
                />
              </FormControl>

              <FormControl>
                <FormLabel>Role</FormLabel>
                <Select
                  name="role"
                  value={formData.role}
                  onChange={handleChange}
                  bg={inputBg}
                  borderRadius="xl"
                >
                  <option value="user">User</option>
                  <option value="admin">Admin</option>
                </Select>
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter gap={3}>
            <Button variant="ghost" onClick={onUserModalClose} borderRadius="xl">
              Cancel
            </Button>
            <Button
              colorScheme="brand"
              onClick={handleSubmit}
              isLoading={isSubmitting}
              borderRadius="xl"
            >
              {selectedUser ? 'Save Changes' : 'Create User'}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </VStack>
  );
}

function LicenseTab({ cardBg, borderColor, textSecondary, inputBg, toast, setLicense }) {
  const [licenseDetails, setLicenseDetails] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [licenseKey, setLicenseKey] = useState('');
  const [validationUrl, setValidationUrl] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isValidating, setIsValidating] = useState(false);

  useEffect(() => {
    fetchLicenseDetails();
  }, []);

  const fetchLicenseDetails = async () => {
    setIsLoading(true);
    try {
      const response = await licenseApi.getDetails();
      setLicenseDetails(response.data);
      if (response.data?.license_key) {
        setLicenseKey(response.data.license_key);
      }
      if (response.data?.validation_url) {
        setValidationUrl(response.data.validation_url);
      }
    } catch (error) {
      console.error('Failed to fetch license details:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleActivate = async () => {
    if (!licenseKey.trim()) {
      toast({ title: 'License key is required', status: 'error', duration: 3000 });
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await licenseApi.activate(licenseKey, validationUrl || null);

      if (response.data) {
        setLicenseDetails(response.data);
        setLicense({
          isValid: response.data.isValid,
          status: response.data.status,
          type: response.data.license_type,
          expiresAt: response.data.expires_at,
        });
        toast({
          title: 'License activated',
          description: response.data.validationMessage || 'License key saved successfully',
          status: 'success',
          duration: 5000,
        });
      }
    } catch (error) {
      toast({
        title: 'Error activating license',
        description: error.message,
        status: 'error',
        duration: 5000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleValidate = async () => {
    setIsValidating(true);
    try {
      const response = await licenseApi.validate();

      if (response.data) {
        setLicenseDetails(response.data);
        setLicense({
          isValid: response.data.isValid,
          status: response.data.status,
          type: response.data.license_type,
          expiresAt: response.data.expires_at,
        });
        toast({
          title: 'Validation successful',
          description: response.data.validationMessage || 'License validated successfully',
          status: 'success',
          duration: 3000,
        });
      }
    } catch (error) {
      toast({
        title: 'Validation failed',
        description: error.message,
        status: 'error',
        duration: 5000,
      });
      fetchLicenseDetails();
    } finally {
      setIsValidating(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'green';
      case 'expired':
        return 'red';
      case 'invalid':
        return 'red';
      default:
        return 'gray';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  return (
    <VStack spacing={6} align="stretch">
      {/* License Activation Card */}
      <Card bg={cardBg} border="1px solid" borderColor={borderColor}>
        <CardHeader pb={2}>
          <HStack spacing={2}>
            <Box p={2} bgGradient="linear(135deg, brand.500 0%, accent.500 100%)" borderRadius="lg">
              <Icon color="white" boxSize={4}>
                <FontAwesomeIcon icon={faShieldAlt} />
              </Icon>
            </Box>
            <Heading size="md">Activate License</Heading>
          </HStack>
        </CardHeader>
        <CardBody>
          {/* License Status Banner */}
          {isLoading ? (
            <Skeleton height="40px" width="250px" borderRadius="full" mb={4} />
          ) : (
            <HStack
              spacing={3}
              p={2}
              px={4}
              mb={4}
              bg={licenseDetails?.isValid ? 'green.50' : 'orange.50'}
              borderRadius="full"
              w="fit-content"
              _dark={{
                bg: licenseDetails?.isValid ? 'green.900' : 'orange.900',
              }}
            >
              <Icon
                color={licenseDetails?.isValid ? 'green.500' : 'orange.500'}
                boxSize={4}
              >
                <FontAwesomeIcon icon={licenseDetails?.isValid ? faCheckCircle : faExclamationTriangle} />
              </Icon>
              <Text fontWeight="600" fontSize="sm">
                {licenseDetails?.isValid
                  ? `License Active${licenseDetails.expires_at ? ` — expires ${formatDate(licenseDetails.expires_at)}` : ''}`
                  : 'No Active License'}
              </Text>
              {licenseDetails?.status && (
                <Badge colorScheme={getStatusColor(licenseDetails.status)} fontSize="xs" borderRadius="full">
                  {licenseDetails.status}
                </Badge>
              )}
            </HStack>
          )}
          <VStack align="stretch" spacing={4}>
            <FormControl>
              <FormLabel>License Key</FormLabel>
              <Input
                value={licenseKey}
                onChange={(e) => setLicenseKey(e.target.value)}
                placeholder="Enter your license key"
                bg={inputBg}
                borderRadius="lg"
                autoComplete="off"
                fontFamily="mono"
              />
              <FormHelperText>
                Your license key is provided when you purchase a subscription.
              </FormHelperText>
            </FormControl>

            <FormControl>
              <FormLabel>Validation Server URL (Optional)</FormLabel>
              <Input
                value={validationUrl}
                onChange={(e) => setValidationUrl(e.target.value)}
                placeholder="https://your-server.com/validate"
                bg={inputBg}
                borderRadius="lg"
              />
              <FormHelperText>
                If provided, the license will be validated against this server.
              </FormHelperText>
            </FormControl>

            <HStack spacing={3} pt={2}>
              <Button
                colorScheme="brand"
                onClick={handleActivate}
                isLoading={isSubmitting}
                loadingText="Activating..."
                borderRadius="lg"
              >
                Activate License
              </Button>
              {licenseDetails?.validation_url && (
                <Button
                  variant="outline"
                  colorScheme="brand"
                  leftIcon={<FontAwesomeIcon icon={faSync} />}
                  onClick={handleValidate}
                  isLoading={isValidating}
                  loadingText="Validating..."
                  borderRadius="lg"
                >
                  Validate Now
                </Button>
              )}
            </HStack>
          </VStack>
        </CardBody>
      </Card>
    </VStack>
  );
}

export default SettingsPage;
